// Sparstatus för automatisk sparning (useAutosave): statusrad och kvittens
// intill ett fält. Används av konsultprofilen och uppdragsbudgeten.
import React from 'react';
import { Box, Button, CircularProgress, Container } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { SAVED_MARK_MS } from '../hooks/useAutosave';

// ── Kvittens intill ett fält: grön bock som tonar bort, eller rött vid fel ──
export function SaveMark({ status }) {
  if (!status) return null;
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 0.5,
    fontSize: '0.75rem', fontWeight: 400, whiteSpace: 'nowrap', verticalAlign: 'middle',
  };
  if (status.state === 'error') return (
    <Box component="span" sx={{ ...base, color: 'error.main', fontWeight: 600 }}>
      <ErrorOutlineIcon sx={{ fontSize: 16 }} />Inte sparat
    </Box>
  );
  return (
    // key = n startar om uttoningen när samma fält sparas igen
    <Box component="span" key={status.n} sx={{
      ...base, color: 'success.main',
      '@keyframes saveMarkFade': {
        '0%, 70%': { opacity: 1 }, '100%': { opacity: 0 },
      },
      animation: `saveMarkFade ${SAVED_MARK_MS}ms ease-in forwards`,
    }}>
      <CheckIcon sx={{ fontSize: 16 }} />Sparat
    </Box>
  );
}

/** Innehållet i statusraden: Sparar… / Alla ändringar sparade / fel med Försök igen. */
export function SparStatusText({ autosave, readOnly, readOnlyText = 'Bara läsbehörighet – ändringar sparas inte' }) {
  if (readOnly) return <>{readOnlyText}</>;
  if (autosave.failing) return (
    <>
      <ErrorOutlineIcon sx={{ fontSize: 18 }} />
      Kunde inte spara – försöker igen
      <Button size="small" color="error" variant="outlined"
        sx={{ ml: 1, py: 0 }} onClick={() => autosave.flush()}>
        Försök igen
      </Button>
    </>
  );
  if (autosave.saving) return <><CircularProgress size={14} color="inherit" />Sparar…</>;
  return (
    <><CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
      Alla ändringar sparade{autosave.lastSavedAt && ' · ' + autosave.lastSavedAt
        .toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</>
  );
}

/** Fast statusrad högst upp vid scroll (konsultprofilen). */
export function SparStatusRad({ autosave, readOnly, maxWidth = 'lg' }) {
  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 10,
      bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
      <Container maxWidth={maxWidth} role="status"
        sx={{ py: 0.75, minHeight: 40, display: 'flex', alignItems: 'center', gap: 1,
          fontSize: '0.85rem',
          color: autosave.failing ? 'error.main' : 'text.secondary' }}>
        <SparStatusText autosave={autosave} readOnly={readOnly} />
      </Container>
    </Box>
  );
}
